import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Plus,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
  CheckCircle,
  XCircle,
  Edit,
  Trash2,
  Search,
  Star,
  StarOff,
  ToggleLeft,
  ToggleRight,
  Info,
  Image,
  Search as SearchIcon,
  Settings,
  Save,
  X,
} from "lucide-react";
import { adminService } from "@b2b/services/api/modules/admin/admin.service";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  TanStackDataTable,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Separator,
  adminCategoriesTableConfig,
  MediaSelector,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@b2b/ui-components";
import { createCategoryColumns } from "./AdminCategories.columns";

// Types for API responses
interface Category {
  id: string;
  name: string;
  type?: string;
  description?: string;
  slug: string;
  parentId?: string;
  isActive: boolean;
  sortOrder: number;
  featured: boolean;
  banner?: string;
  icon?: string;
  coverImage?: string;
  metaTitle?: string;
  metaDescription?: string;
  filteringAttributes?: string[];
  createdAt: string;
  updatedAt: string;
  parent?: Category;
  children?: Category[];
  productCount: number;
  level?: number;
}

interface CategoryFormData {
  name: string;
  type: string;
  description: string;
  slug: string;
  parentId: string;
  sortOrder: number;
  banner: string;
  icon: string;
  coverImage: string;
  metaTitle: string;
  metaDescription: string;
  filteringAttributes: string[];
  isActive: boolean;
  featured: boolean;
}

// Step configuration for the form
const getSteps = () => [
  {
    id: "basic",
    title: "Basic Info",
    icon: Info,
    description: "Category name, type, and description",
  },
  {
    id: "images",
    title: "Images",
    icon: Image,
    description: "Banner, icon, and cover images",
  },
  {
    id: "seo",
    title: "SEO",
    icon: SearchIcon,
    description: "Meta title and description",
  },
  {
    id: "settings",
    title: "Settings",
    icon: Settings,
    description: "Status, featured, and filters",
  },
];

/** Flatten hierarchy for table view; attach minimal `parent` for the Parent column. */
function flattenCategoryTree(
  nodes: Category[],
  parentNode: Category | null = null
): Category[] {
  const rows: Category[] = [];
  for (const node of nodes) {
    const children = node.children ?? [];
    const { children: _ch, parent: _pr, ...rest } = node;
    const row: Category = {
      ...(rest as Category),
      children: undefined,
      parent:
        parentNode != null
          ? ({
              id: parentNode.id,
              name: parentNode.name,
              slug: parentNode.slug,
              parentId: parentNode.parentId,
              isActive: parentNode.isActive,
              sortOrder: parentNode.sortOrder,
              featured: parentNode.featured,
              productCount: parentNode.productCount,
            } as Category)
          : undefined,
    };
    rows.push(row);
    if (children.length > 0) {
      rows.push(...flattenCategoryTree(children as Category[], node));
    }
  }
  return rows;
}

const AdminCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"table" | "tree">("table");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deleteCategoryId, setDeleteCategoryId] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);
  const [expandedTreeNodes, setExpandedTreeNodes] = useState<
    Record<string, boolean>
  >({});
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<CategoryFormData>({
    name: "",
    type: "",
    description: "",
    slug: "",
    parentId: "none",
    sortOrder: 0,
    banner: "",
    icon: "",
    coverImage: "",
    metaTitle: "",
    metaDescription: "",
    filteringAttributes: [],
    isActive: true,
    featured: false,
  });
  const [selectedParentId, setSelectedParentId] = useState<string>("none");
  const [selectedChildId, setSelectedChildId] = useState<string>("none");
  const [nameFieldError, setNameFieldError] = useState("");
  const [sortOrderInput, setSortOrderInput] = useState("");

  // Media selection state
  const [bannerMedia, setBannerMedia] = useState<{
    id: string;
    url: string;
    thumbnailUrl?: string;
    originalName: string;
  } | null>(null);
  const [iconMedia, setIconMedia] = useState<{
    id: string;
    url: string;
    thumbnailUrl?: string;
    originalName: string;
  } | null>(null);
  const [coverImageMedia, setCoverImageMedia] = useState<{
    id: string;
    url: string;
    thumbnailUrl?: string;
    originalName: string;
  } | null>(null);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [currentMediaType, setCurrentMediaType] = useState<
    "banner" | "icon" | "coverImage"
  >("banner");
  const [originalMediaSelection, setOriginalMediaSelection] = useState<
    Array<{
      id: string;
      url: string;
      thumbnailUrl?: string;
      originalName: string;
    }>
  >([]);

  const showToast = useCallback(
    (type: "success" | "error" | "info", message: string) => {
      setToast({ type, message });
      setTimeout(() => setToast(null), 5000);
    },
    []
  );

  // Fetch categories (forceRefresh bypasses client request cache after mutations)
  const fetchCategories = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const data = await adminService.getCategories(true, forceRefresh);
      setCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const flatTableRows = useMemo(() => {
    const flat = flattenCategoryTree(categories);
    if (!searchTerm.trim()) return flat;
    const q = searchTerm.toLowerCase();
    return flat.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.description?.toLowerCase().includes(q) ?? false) ||
        c.slug.toLowerCase().includes(q)
    );
  }, [categories, searchTerm]);

  // Create category
  const handleCreateCategory = async () => {
    // Validate required fields
    if (!formData.name.trim()) {
      setNameFieldError("Category name is required");
      setCurrentStep(0);
      return;
    }

    try {
      setNameFieldError("");
      const submitData = {
        ...formData,
        parentId: formData.parentId === "none" ? undefined : formData.parentId,
        // Extract URLs from selected media
        banner: bannerMedia?.url || formData.banner || "",
        icon: iconMedia?.url || formData.icon || "",
        coverImage: coverImageMedia?.url || formData.coverImage || "",
      };

      const response = await adminService.createCategory(submitData);

      if (response.success) {
        await fetchCategories(true);
        setIsCreateDialogOpen(false);
        resetForm();
        showToast("success", "Category created successfully.");
      } else {
        showToast("error", response.message || "Failed to create category");
      }
    } catch (error) {
      console.error("Error creating category:", error);
      showToast("error", "Failed to create category. Please try again.");
    }
  };

  // Update category
  const handleUpdateCategory = async () => {
    if (!editingCategory) return;

    // Validate required fields
    if (!formData.name.trim()) {
      setNameFieldError("Category name is required");
      setCurrentStep(0);
      return;
    }

    try {
      setNameFieldError("");
      const submitData = {
        ...formData,
        parentId: formData.parentId === "none" ? undefined : formData.parentId,
        // Extract URLs from selected media
        banner: bannerMedia?.url || formData.banner || "",
        icon: iconMedia?.url || formData.icon || "",
        coverImage: coverImageMedia?.url || formData.coverImage || "",
      };

      const response = await adminService.updateCategory(
        editingCategory.id,
        submitData
      );

      if (response.success) {
        await fetchCategories(true);
        setIsEditDialogOpen(false);
        setEditingCategory(null);
        resetForm();
        showToast("success", "Category updated successfully.");
      } else {
        showToast("error", response.message || "Failed to update category");
      }
    } catch (error) {
      console.error("Error updating category:", error);
      showToast("error", "Failed to update category. Please try again.");
    }
  };

  // Delete category
  const handleDeleteCategory = async (id: string) => {
    try {
      const response = await adminService.deleteCategory(id);

      if (response.success) {
        await fetchCategories(true);
        setDeleteCategoryId(null);
      } else {
        console.error("Failed to delete category");
      }
    } catch (error) {
      console.error("Error deleting category:", error);
    }
  };

  // Toggle featured status
  const toggleFeatured = async (id: string) => {
    try {
      const response = await adminService.toggleCategoryFeatured(id);

      if (response.success) {
        await fetchCategories(true);
      }
    } catch (error) {
      console.error("Error toggling featured status:", error);
    }
  };

  // Toggle active status
  const toggleActive = async (id: string) => {
    try {
      const response = await adminService.toggleCategoryActive(id);

      if (response.success) {
        await fetchCategories(true);
      }
    } catch (error) {
      console.error("Error toggling active status:", error);
    }
  };

  // Open edit dialog
  const openEditDialog = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      type: category.type || "",
      description: category.description || "",
      slug: category.slug,
      parentId: category.parentId || "none",
      sortOrder: category.sortOrder,
      banner: category.banner || "",
      icon: category.icon || "",
      coverImage: category.coverImage || "",
      metaTitle: category.metaTitle || "",
      metaDescription: category.metaDescription || "",
      filteringAttributes: category.filteringAttributes || [],
      isActive: category.isActive,
      featured: category.featured,
    });
    setSortOrderInput(
      category.sortOrder > 0 ? String(category.sortOrder) : ""
    );

    // Initialize media from URLs (create placeholder objects)
    setBannerMedia(
      category.banner
        ? {
            id: `banner-${category.id}`,
            url: category.banner,
            originalName: "Banner Image",
          }
        : null
    );
    setIconMedia(
      category.icon
        ? {
            id: `icon-${category.id}`,
            url: category.icon,
            originalName: "Icon Image",
          }
        : null
    );
    setCoverImageMedia(
      category.coverImage
        ? {
            id: `cover-${category.id}`,
            url: category.coverImage,
            originalName: "Cover Image",
          }
        : null
    );

    // Set cascading dropdown states for edit
    if (category.parentId) {
      // Find the parent category to determine if it's root or level 1
      const parent = categories.find((cat) => cat.id === category.parentId);
      if (parent && !parent.parentId) {
        // Parent is root, so this is a level 1 category
        setSelectedParentId(category.parentId);
        setSelectedChildId("none");
      } else if (parent && parent.parentId) {
        // Parent is level 1, so this is a level 2 category
        setSelectedParentId(parent.parentId);
        setSelectedChildId(category.parentId);
      }
    } else {
      // This is a root category
      setSelectedParentId("none");
      setSelectedChildId("none");
    }

    setCurrentStep(0);
    setIsEditDialogOpen(true);
    setNameFieldError("");
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      type: "",
      description: "",
      slug: "",
      parentId: "none",
      sortOrder: 0,
      banner: "",
      icon: "",
      coverImage: "",
      metaTitle: "",
      metaDescription: "",
      filteringAttributes: [],
      isActive: true,
      featured: false,
    });
    setSelectedParentId("none");
    setSelectedChildId("none");
    setCurrentStep(0);
    setNameFieldError("");
    setSortOrderInput("");
    // Reset media selections
    setBannerMedia(null);
    setIconMedia(null);
    setCoverImageMedia(null);
  };

  const handleCreateDialogOpenChange = (open: boolean) => {
    setIsCreateDialogOpen(open);
    if (open) {
      // Always open create dialog with a clean form state.
      resetForm();
      setEditingCategory(null);
      return;
    }
    if (!open) {
      resetForm();
    }
  };

  const handleEditDialogOpenChange = (open: boolean) => {
    setIsEditDialogOpen(open);
    if (!open) {
      setEditingCategory(null);
      resetForm();
    }
  };

  // Add filtering attribute
  const addFilteringAttribute = (attribute: string) => {
    if (
      attribute.trim() &&
      !formData.filteringAttributes.includes(attribute.trim())
    ) {
      setFormData({
        ...formData,
        filteringAttributes: [
          ...formData.filteringAttributes,
          attribute.trim(),
        ],
      });
    }
  };

  // Remove filtering attribute
  const removeFilteringAttribute = (index: number) => {
    setFormData({
      ...formData,
      filteringAttributes: formData.filteringAttributes.filter(
        (_, i) => i !== index
      ),
    });
  };

  // Get root categories (Level 0) for first dropdown
  const getRootCategories = () => {
    return categories.filter((cat) => !cat.parentId);
  };

  // Get Level 1 children of selected parent
  const getLevel1Children = (parentId: string) => {
    if (parentId === "none") return [];
    const parent = categories.find((cat) => cat.id === parentId);
    return parent?.children || [];
  };

  // Get Level 2 children of selected Level 1 category
  const getLevel2Children = (level1Id: string) => {
    if (level1Id === "none") return [];
    const level1Category = categories.find((cat) => cat.id === level1Id);
    return level1Category?.children || [];
  };

  // Handle parent selection change
  const handleParentChange = (parentId: string) => {
    setSelectedParentId(parentId);
    setSelectedChildId("none");
    setFormData({ ...formData, parentId: parentId });
  };

  // Handle Level 1 child selection change
  const handleLevel1ChildChange = (childId: string) => {
    setSelectedChildId(childId);
    setFormData({ ...formData, parentId: childId });
  };

  // Media selection handlers
  const handleChooseMedia = (mediaType: "banner" | "icon" | "coverImage") => {
    setCurrentMediaType(mediaType);

    // Store the current selection as original
    if (mediaType === "banner") {
      setOriginalMediaSelection(bannerMedia ? [bannerMedia] : []);
    } else if (mediaType === "icon") {
      setOriginalMediaSelection(iconMedia ? [iconMedia] : []);
    } else if (mediaType === "coverImage") {
      setOriginalMediaSelection(coverImageMedia ? [coverImageMedia] : []);
    }

    setIsMediaModalOpen(true);
  };

  const handleMediaSelection = (
    selectedMediaItems: Array<{
      id: string;
      url: string;
      thumbnailUrl?: string;
      originalName: string;
    }>,
    shouldCloseModal?: boolean
  ) => {
    // If empty array, it means Cancel was clicked - restore original selection
    if (selectedMediaItems.length === 0) {
      if (currentMediaType === "banner") {
        setBannerMedia(originalMediaSelection[0] || null);
      } else if (currentMediaType === "icon") {
        setIconMedia(originalMediaSelection[0] || null);
      } else if (currentMediaType === "coverImage") {
        setCoverImageMedia(originalMediaSelection[0] || null);
      }
      setIsMediaModalOpen(false);
      return;
    }

    // Apply the new selection
    const selectedMedia = selectedMediaItems[0] || null;
    if (currentMediaType === "banner") {
      setBannerMedia(selectedMedia);
      setFormData({ ...formData, banner: selectedMedia?.url || "" });
    } else if (currentMediaType === "icon") {
      setIconMedia(selectedMedia);
      setFormData({ ...formData, icon: selectedMedia?.url || "" });
    } else if (currentMediaType === "coverImage") {
      setCoverImageMedia(selectedMedia);
      setFormData({ ...formData, coverImage: selectedMedia?.url || "" });
    }

    // Only close modal if explicitly requested (Add Files button)
    if (shouldCloseModal) {
      setIsMediaModalOpen(false);
    }
  };

  const handleRemoveMedia = (mediaType: "banner" | "icon" | "coverImage") => {
    if (mediaType === "banner") {
      setBannerMedia(null);
      setFormData({ ...formData, banner: "" });
    } else if (mediaType === "icon") {
      setIconMedia(null);
      setFormData({ ...formData, icon: "" });
    } else if (mediaType === "coverImage") {
      setCoverImageMedia(null);
      setFormData({ ...formData, coverImage: "" });
    }
  };

  // Render step content
  const renderStepContent = () => {
    const steps = getSteps();
    const currentStepData = steps[currentStep];

    const field = "flex flex-col gap-2";
    const labelCls =
      "text-sm font-medium text-foreground leading-snug peer-disabled:cursor-not-allowed peer-disabled:opacity-70";
    const hint = "text-xs text-muted-foreground leading-relaxed";

    switch (currentStepData.id) {
      case "basic":
        return (
          <div className="mx-auto w-full max-w-3xl space-y-6">
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-foreground">
                Category details
              </h4>
              <p className={hint}>
                Primary name and classification shown across the storefront.
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
                <div className={field}>
                  <Label htmlFor="name" className={labelCls}>
                    Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => {
                      const nextName = e.target.value;
                      setFormData({ ...formData, name: nextName });
                      if (nameFieldError && nextName.trim()) {
                        setNameFieldError("");
                      }
                    }}
                    placeholder="Category name"
                    className="h-10 w-full"
                    aria-invalid={!!nameFieldError}
                  />
                  {nameFieldError ? (
                    <p className="text-xs text-destructive">{nameFieldError}</p>
                  ) : null}
                </div>
                <div className={field}>
                  <Label htmlFor="type" className={labelCls}>
                    Type
                  </Label>
                  <Input
                    id="type"
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({ ...formData, type: e.target.value })
                    }
                    placeholder="e.g. Product category"
                    className="h-10 w-full"
                  />
                </div>
              </div>
            </div>

            <div className={field}>
              <Label htmlFor="description" className={labelCls}>
                Description
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Short summary for listings and admin reference"
                rows={4}
                className="min-h-[100px] resize-y"
              />
            </div>

            <div className="space-y-4 border-t border-border/60 pt-4">
              <h4 className="text-sm font-semibold text-foreground">
                Hierarchy & ordering
              </h4>
              <p className={hint}>
                Choose where this category lives in the tree and how it sorts.
              </p>

              <div className={field}>
                <Label htmlFor="parentId" className={labelCls}>
                  Parent category
                </Label>
                <Select
                  value={selectedParentId}
                  onValueChange={handleParentChange}
                >
                  <SelectTrigger id="parentId" className="h-10 w-full">
                    <SelectValue placeholder="Select parent category" />
                  </SelectTrigger>
                  <SelectContent className="!bg-white !border-gray-300 !shadow-lg z-50 min-w-[200px] rounded-md">
                    <SelectItem
                      value="none"
                      className="hover:!bg-gray-100 cursor-pointer"
                    >
                      No parent (root category)
                    </SelectItem>
                    {getRootCategories().map((category) => (
                      <SelectItem
                        key={category.id}
                        value={category.id}
                        className="hover:!bg-gray-100 cursor-pointer"
                      >
                        {category.name} (root)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className={hint}>
                  {selectedParentId === "none"
                    ? "Creates a root category (level 0)."
                    : "Optionally pick a subcategory below to nest further."}
                </p>
              </div>

              {selectedParentId !== "none" &&
                getLevel1Children(selectedParentId).length > 0 && (
                  <div className={field}>
                    <Label htmlFor="level1Child" className={labelCls}>
                      Subcategory (level 1)
                    </Label>
                    <Select
                      value={selectedChildId}
                      onValueChange={handleLevel1ChildChange}
                    >
                      <SelectTrigger id="level1Child" className="h-10 w-full">
                        <SelectValue placeholder="Select subcategory (optional)" />
                      </SelectTrigger>
                      <SelectContent className="!bg-white !border-gray-300 !shadow-lg z-50 min-w-[200px] rounded-md">
                        <SelectItem
                          value="none"
                          className="hover:!bg-gray-100 cursor-pointer"
                        >
                          No subcategory (stay at level 1)
                        </SelectItem>
                        {getLevel1Children(selectedParentId).map((category) => (
                          <SelectItem
                            key={category.id}
                            value={category.id}
                            className="hover:!bg-gray-100 cursor-pointer"
                          >
                            {category.name} (level 1)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className={hint}>
                      {selectedChildId === "none"
                        ? "New category will be level 1 under the selected parent."
                        : "New category will be level 2 under the subcategory."}
                    </p>
                  </div>
                )}

              <div className={`${field} border-t border-border/60 pt-4`}>
                <Label htmlFor="sortOrder" className={labelCls}>
                  Sort order
                </Label>
                <Input
                  id="sortOrder"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={sortOrderInput}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    if (!/^\d*$/.test(nextValue)) return;
                    setSortOrderInput(nextValue);
                    setFormData({
                      ...formData,
                      sortOrder: nextValue === "" ? 0 : Number(nextValue),
                    });
                  }}
                  placeholder="Enter sort order"
                  className="h-10 w-full max-w-[200px]"
                />
                <p className={hint}>Higher values appear first among siblings.</p>
              </div>
            </div>
          </div>
        );

      case "images":
        return (
          <div className="mx-auto w-full max-w-5xl space-y-8">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Optional visuals for menus and category pages. Use the gallery to
              pick assets.
            </p>
            <div className="grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-8">
              {/* Banner Image */}
              <div className="space-y-3">
                <Label htmlFor="banner" className={labelCls}>
                  Banner image
                </Label>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleChooseMedia("banner")}
                      className="flex-1"
                    >
                      <Image className="h-4 w-4 mr-2" />
                      {bannerMedia ? "Change Banner" : "Choose from Gallery"}
                    </Button>
                    {bannerMedia && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMedia("banner")}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <p className={hint}>Minimum 150 × 150 px recommended.</p>
                  {(bannerMedia || formData.banner) && (
                    <div className="rounded-lg border border-border/60 bg-background p-2">
                      <img
                        src={bannerMedia?.url || formData.banner}
                        alt="Banner preview"
                        className="h-20 w-full max-w-[8rem] object-cover rounded-md"
                      />
                      {bannerMedia && (
                        <p className={`${hint} mt-2 truncate`}>
                          {bannerMedia.originalName}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Icon Image */}
              <div className="space-y-3">
                <Label htmlFor="icon" className={labelCls}>
                  Icon image
                </Label>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleChooseMedia("icon")}
                      className="flex-1"
                    >
                      <Image className="h-4 w-4 mr-2" />
                      {iconMedia ? "Change Icon" : "Choose from Gallery"}
                    </Button>
                    {iconMedia && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMedia("icon")}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <p className={hint}>Minimum 16 × 16 px; square works best.</p>
                  {(iconMedia || formData.icon) && (
                    <div className="rounded-lg border border-border/60 bg-background p-2 inline-block">
                      <img
                        src={iconMedia?.url || formData.icon}
                        alt="Icon preview"
                        className="h-10 w-10 object-cover rounded-md"
                      />
                      {iconMedia && (
                        <p className={`${hint} mt-2 max-w-[10rem] truncate`}>
                          {iconMedia.originalName}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Cover Image */}
              <div className="space-y-3">
                <Label htmlFor="coverImage" className={labelCls}>
                  Cover image
                </Label>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleChooseMedia("coverImage")}
                      className="flex-1"
                    >
                      <Image className="h-4 w-4 mr-2" />
                      {coverImageMedia ? "Change Cover" : "Choose from Gallery"}
                    </Button>
                    {coverImageMedia && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMedia("coverImage")}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <p className={hint}>Minimum 260 × 260 px recommended.</p>
                  {(coverImageMedia || formData.coverImage) && (
                    <div className="rounded-lg border border-border/60 bg-background p-2 inline-block">
                      <img
                        src={coverImageMedia?.url || formData.coverImage}
                        alt="Cover preview"
                        className="h-24 w-24 object-cover rounded-md"
                      />
                      {coverImageMedia && (
                        <p className={`${hint} mt-2 max-w-[10rem] truncate`}>
                          {coverImageMedia.originalName}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case "seo":
        return (
          <div className="mx-auto w-full max-w-3xl space-y-6">
            <div>
              <h4 className="text-sm font-semibold text-foreground">SEO</h4>
              <p className={`${hint} mt-1`}>
                Shown in search results when applicable. Keep titles concise.
              </p>
            </div>
            <div className={field}>
              <Label htmlFor="metaTitle" className={labelCls}>
                Meta title
              </Label>
              <Input
                id="metaTitle"
                value={formData.metaTitle}
                onChange={(e) =>
                  setFormData({ ...formData, metaTitle: e.target.value })
                }
                placeholder="Meta title for SEO"
                className="h-10 w-full"
              />
            </div>

            <div className={field}>
              <Label htmlFor="metaDescription" className={labelCls}>
                Meta description
              </Label>
              <Textarea
                id="metaDescription"
                value={formData.metaDescription}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    metaDescription: e.target.value,
                  })
                }
                placeholder="Meta description for SEO"
                rows={4}
                className="min-h-[110px] resize-y"
              />
            </div>
          </div>
        );

      case "settings":
        return (
          <div className="mx-auto w-full max-w-3xl space-y-6">
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-foreground">Visibility</h4>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-8">
                <div className="flex items-center gap-3">
                  <Switch
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, isActive: checked })
                    }
                  />
                  <div className="flex flex-col gap-0.5">
                    <Label htmlFor="isActive" className={labelCls}>
                      Active
                    </Label>
                    <span className={hint}>
                      Inactive categories stay hidden from buyers.
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    id="featured"
                    checked={formData.featured}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, featured: checked })
                    }
                  />
                  <div className="flex flex-col gap-0.5">
                    <Label htmlFor="featured" className={labelCls}>
                      Featured
                    </Label>
                    <span className={hint}>
                      Surfaces in featured placements when configured.
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4 border-t border-border/60 pt-4">
              <div className={field}>
                <span className={labelCls}>Filtering attributes</span>
                <p className={hint}>
                  Tags used for filters on listing pages (e.g. size, material).
                </p>
              </div>
              <div className="space-y-4">
                <div className="flex min-h-[2rem] flex-wrap gap-2">
                  {formData.filteringAttributes.map((attr, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="flex items-center gap-1.5 px-2.5 py-1"
                    >
                      {attr}
                      <button
                        type="button"
                        onClick={() => removeFilteringAttribute(index)}
                        className="ml-0.5 rounded-sm hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label={`Remove ${attr}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                  <Input
                    placeholder="Add attribute (e.g., Size, Fabric)"
                    className="h-10 flex-1"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const value = e.currentTarget.value.trim();
                        if (value) {
                          addFilteringAttribute(value);
                          e.currentTarget.value = "";
                        }
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="h-10 shrink-0 sm:w-auto"
                    onClick={() => {
                      const input = document.querySelector(
                        'input[placeholder="Add attribute (e.g., Size, Fabric)"]'
                      ) as HTMLInputElement;
                      const value = input?.value.trim();
                      if (value) {
                        addFilteringAttribute(value);
                        input.value = "";
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Create column definitions with memoization
  const columns = useMemo(
    () =>
      createCategoryColumns({
        onEdit: openEditDialog,
        onToggleActive: toggleActive,
        onToggleFeatured: toggleFeatured,
        onDelete: handleDeleteCategory,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    // Keep all categories collapsed by default when tree data loads.
    setExpandedTreeNodes((prev) => {
      const next = { ...prev };
      categories
        .filter((category) => !category.parentId)
        .forEach((category) => {
          if (next[category.id] === undefined) {
            next[category.id] = false;
          }
        });
      return next;
    });
  }, [categories]);

  const toggleTreeNode = (categoryId: string) => {
    setExpandedTreeNodes((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const renderTreeNode = (category: Category, depth = 0) => {
    const hasChildren = !!category.children?.length;
    const isExpanded = expandedTreeNodes[category.id] ?? false;

    return (
      <div key={category.id} className="space-y-2">
        <div
          className="flex items-center justify-between gap-3 rounded-md border border-border/70 bg-background px-3 py-2"
          style={{ marginLeft: `${depth * 20}px` }}
        >
          <div className="min-w-0 flex items-center gap-2">
            {hasChildren ? (
              <button
                type="button"
                className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={() => toggleTreeNode(category.id)}
                aria-label={isExpanded ? "Collapse category" : "Expand category"}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : (
              <span className="inline-flex h-6 w-6 items-center justify-center text-muted-foreground/50" />
            )}

            {hasChildren ? (
              isExpanded ? (
                <FolderOpen className="h-4 w-4 shrink-0 text-amber-600" />
              ) : (
                <Folder className="h-4 w-4 shrink-0 text-amber-600" />
              )
            ) : (
              <Folder className="h-4 w-4 shrink-0 text-muted-foreground/70" />
            )}

            <div className="min-w-0 flex items-center gap-2">
              <span className="truncate font-medium text-foreground">
                {category.name}
              </span>
              {category.type ? (
                <Badge variant="outline" className="text-xs">
                  {category.type}
                </Badge>
              ) : null}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">Active</span>
              <Switch
                checked={category.isActive}
                onCheckedChange={() => toggleActive(category.id)}
                aria-label={category.isActive ? "Deactivate category" : "Activate category"}
              />
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">Featured</span>
              <Switch
                checked={category.featured}
                onCheckedChange={() => toggleFeatured(category.id)}
                aria-label={category.featured ? "Remove featured" : "Mark featured"}
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => openEditDialog(category)}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => handleDeleteCategory(category.id)}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="space-y-2 border-l border-border/60 pl-2">
            {category.children!.map((child) => renderTreeNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {toast && (
        <div
          className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-[9999] ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : toast.type === "error"
                ? "bg-red-500 text-white"
                : "bg-blue-500 text-white"
          }`}
          role="alert"
        >
          <div className="flex items-center gap-2">
            {toast.type === "success" ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : toast.type === "error" ? (
              <XCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <Info className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="flex-1 font-medium text-sm">{toast.message}</p>
            <button
              type="button"
              onClick={() => setToast(null)}
              className="ml-2 hover:opacity-80 transition-opacity flex-shrink-0"
              aria-label="Close notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Categories</h1>
          <p className="text-gray-500">
            Manage product categories with 3-level hierarchy (Parent → Child →
            Child)
          </p>
        </div>
        <Dialog
          open={isCreateDialogOpen}
          onOpenChange={handleCreateDialogOpenChange}
        >
          <DialogTrigger asChild>
            <Button variant="primary">
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </DialogTrigger>
          <DialogContent
            className="max-w-6xl max-h-[90vh] overflow-hidden p-0 bg-white"
            showCloseButton={false}
          >
            <div className="flex h-[85vh]">
              {/* Sidebar Navigation */}
              <div className="w-64 shrink-0 border-r border-border/80 bg-muted/40 p-6">
                <div className="mb-8">
                  <h2 className="text-lg font-semibold tracking-tight text-foreground">
                    Create category
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                    Add a new category to your catalog.
                  </p>
                </div>

                <nav className="space-y-1.5">
                  {getSteps().map((step, index) => {
                    const Icon = step.icon;
                    return (
                      <button
                        key={step.id}
                        type="button"
                        onClick={() => setCurrentStep(index)}
                        className={`w-full flex items-start gap-3 rounded-lg border-l-[3px] pl-2.5 pr-3 py-3 text-left transition-colors ${
                          currentStep === index
                            ? "border-orange-500 bg-orange-500 text-white shadow-sm"
                            : "border-transparent text-foreground hover:bg-muted/75"
                        }`}
                      >
                        <Icon
                          className={`w-5 h-5 shrink-0 mt-0.5 ${
                            currentStep === index
                              ? "text-primary"
                              : "text-muted-foreground"
                          }`}
                        />
                        <div className="min-w-0 space-y-0.5">
                          <span
                            className={`text-sm block ${
                              currentStep === index
                                ? "font-semibold text-primary"
                                : "font-medium text-foreground"
                            }`}
                          >
                            {step.title}
                          </span>
                          <p
                            className={`text-xs leading-snug ${
                              currentStep === index
                                ? "text-primary/80"
                                : "text-muted-foreground"
                            }`}
                          >
                            {step.description}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Main Content */}
              <div className="flex min-w-0 flex-1 flex-col bg-background">
                <div className="border-b border-border/80 bg-muted/20 px-6 py-4">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="space-y-1 pr-4">
                      <h3 className="text-lg font-semibold text-foreground tracking-tight">
                        {getSteps()[currentStep].title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
                        {getSteps()[currentStep].description}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      <Button
                        variant="outline"
                        onClick={() => handleCreateDialogOpenChange(false)}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                      <Button variant="primary" onClick={handleCreateCategory}>
                        <Save className="h-4 w-4 mr-2" />
                        Create Category
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-6">
                  {renderStepContent()}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Categories</CardTitle>
            <div className="flex items-center space-x-2">
            <div className="relative mr-2">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  placeholder="Search categories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 w-64"
                />
              </div>
              <div className="inline-flex rounded-md border border-border/70 p-0">
              {/* <div className="relative mr-2">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  placeholder="Search categories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 w-64"
                />
              </div> */}
                <Button
                  type="button"
                  size="sm"
                  variant={viewMode === "table" ? "primary" : "ghost"}
                  onClick={() => setViewMode("table")}
                >
                  Table view
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={viewMode === "tree" ? "primary" : "ghost"}
                  onClick={() => setViewMode("tree")}
                >
                  Tree view
                </Button>
              </div>
              
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : viewMode === "table" ? (
            <TanStackDataTable
              data={flatTableRows}
              columns={columns}
              isLoading={loading}
              emptyMessage="No categories found. Create your first category to get started."
              styles={{
                headerBg: adminCategoriesTableConfig.styles?.headerBg,
                headerText: adminCategoriesTableConfig.styles?.headerText,
                rowHover: adminCategoriesTableConfig.styles?.rowHover,
              }}
              config={{
                enableSorting: true,
                enablePagination: true,
                pageSize: 15,
                enableZebraStriping: false,
                height: "h-auto",
                showBorders: true,
                disableHorizontalScroll: true,
              }}
            />
          ) : categories.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No categories found. Create your first category to get started.
            </div>
          ) : (
            <div className="max-h-[min(70vh,720px)] space-y-3 overflow-y-auto pr-1">
              {categories.map((category) => renderTreeNode(category, 0))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={handleEditDialogOpenChange}>
        <DialogContent
          className="max-w-6xl max-h-[90vh] overflow-hidden p-0 bg-white"
          showCloseButton={false}
        >
          <div className="flex h-[85vh]">
            {/* Sidebar Navigation */}
            <div className="w-64 shrink-0 border-r border-border/80 bg-muted/40 p-6">
              <div className="mb-8">
                <h2 className="text-lg font-semibold tracking-tight text-foreground">
                  Edit category
                </h2>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                  Update category information.
                </p>
              </div>

              <nav className="space-y-1.5">
                {getSteps().map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <button
                      key={step.id}
                      type="button"
                      onClick={() => setCurrentStep(index)}
                      className={`w-full flex items-start gap-3 rounded-lg border-l-[3px] pl-2.5 pr-3 py-3 text-left transition-colors ${
                        currentStep === index
                          ? "border-orange-500 bg-orange-500 text-white shadow-sm"
                          : "border-transparent text-foreground hover:bg-muted/75"
                      }`}
                    >
                      <Icon
                        className={`w-5 h-5 shrink-0 mt-0.5 ${
                          currentStep === index
                            ? "text-primary"
                            : "text-muted-foreground"
                        }`}
                      />
                      <div className="min-w-0 space-y-0.5">
                        <span
                          className={`text-sm block ${
                            currentStep === index
                              ? "font-semibold text-primary"
                              : "font-medium text-foreground"
                          }`}
                        >
                          {step.title}
                        </span>
                        <p
                          className={`text-xs leading-snug ${
                            currentStep === index
                              ? "text-primary/80"
                              : "text-muted-foreground"
                          }`}
                        >
                          {step.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Main Content */}
            <div className="flex min-w-0 flex-1 flex-col bg-background">
              <div className="border-b border-border/80 bg-muted/20 px-6 py-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="space-y-1 pr-4">
                    <h3 className="text-lg font-semibold text-foreground tracking-tight">
                      {getSteps()[currentStep].title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
                      {getSteps()[currentStep].description}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleEditDialogOpenChange(false)}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button variant="primary" onClick={handleUpdateCategory}>
                      <Save className="h-4 w-4 mr-2" />
                      Update Category
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-6">
                {renderStepContent()}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Media Selection Modal */}
      <Dialog open={isMediaModalOpen} onOpenChange={setIsMediaModalOpen}>
        <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-hidden bg-white border-2 border-orange-200 shadow-2xl mx-4 my-4">
          <DialogHeader className="bg-gradient-to-r from-orange-50 to-orange-100 border-b border-orange-200 p-6">
            <DialogTitle className="text-xl font-semibold text-orange-900">
              {currentMediaType === "banner" && "Select Banner Image"}
              {currentMediaType === "icon" && "Select Icon Image"}
              {currentMediaType === "coverImage" && "Select Cover Image"}
            </DialogTitle>
            <DialogDescription className="text-orange-700 mt-2">
              Choose from existing media or upload new files for your category.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[70vh] bg-white p-6">
            <MediaSelector
              selectedMedia={
                currentMediaType === "banner"
                  ? bannerMedia
                    ? [bannerMedia]
                    : []
                  : currentMediaType === "icon"
                    ? iconMedia
                      ? [iconMedia]
                      : []
                    : coverImageMedia
                      ? [coverImageMedia]
                      : []
              }
              onMediaChange={(mediaItems, shouldCloseModal) =>
                handleMediaSelection(mediaItems, shouldCloseModal)
              }
              maxSelection={1}
              className="w-full"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCategories;
